/**
 * The plan grid's editing model, as pure functions — selection, fill, paste and undo.
 *
 * Split out of PlanGrid.tsx deliberately: this is the part with real logic and real edge
 * cases (clamping a paste at the grid edge, normalising a backwards drag), and it is
 * testable without mounting a 1,440-cell React tree.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY A REF-BACKED MAP AND NOT useState
 * ──────────────────────────────────────────────────────────────────────────────
 * `src/components/admin/RateCardManager.tsx` is the state precedent for this grid —
 * flat composite-key draft, `inputMode="decimal"` with string values, `""` meaning absent
 * rather than zero, regex-gated writes, one batched save. Two things in it break at
 * 1,440 cells and are designed out here:
 *
 *   1. It clones the entire draft on every keystroke (`setDraft({ ...working, … })`).
 *      At 1,440 cells that is a 1,440-entry object rebuilt per character.
 *   2. Its dirty check is a double `JSON.stringify` per render.
 *
 * Instead: the draft lives in a `Map` behind a ref (O(1) write, never cloned), and a
 * per-row version counter is the only React state a keystroke touches — so one row of 36
 * inputs re-renders, not 1,440. Dirty tracking is a `Set` of keys, added on diff and
 * deleted on revert, so "is anything dirty" is `.size > 0` rather than a deep compare.
 */

/** `${lineId}:${month}` — the flat composite key, same shape as RateCardManager's. */
export type CellKey = string;

export function cellKey(lineId: string, month: string): CellKey {
  return `${lineId}:${month}`;
}

/** A cell's position in the visible grid. */
export interface CellRef {
  row: number;
  col: number;
}

/**
 * A selection is an anchor plus an extent — not a normalised rectangle.
 *
 * The anchor is where the selection started and is where a paste lands and where a fill
 * reads its source value from. Keeping them distinct is what makes Shift+arrow shrink
 * back toward the anchor rather than flipping the rectangle.
 */
export interface Selection {
  anchor: CellRef;
  extent: CellRef;
}

export interface Rect {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

export function selectionRect(selection: Selection): Rect {
  return {
    top: Math.min(selection.anchor.row, selection.extent.row),
    bottom: Math.max(selection.anchor.row, selection.extent.row),
    left: Math.min(selection.anchor.col, selection.extent.col),
    right: Math.max(selection.anchor.col, selection.extent.col),
  };
}

export function rectContains(rect: Rect, row: number, col: number): boolean {
  return row >= rect.top && row <= rect.bottom && col >= rect.left && col <= rect.right;
}

export function isSingleCell(selection: Selection): boolean {
  return (
    selection.anchor.row === selection.extent.row && selection.anchor.col === selection.extent.col
  );
}

/** Clamp a reference inside the grid. */
export function clampRef(ref: CellRef, rowCount: number, colCount: number): CellRef {
  return {
    row: Math.max(0, Math.min(rowCount - 1, ref.row)),
    col: Math.max(0, Math.min(colCount - 1, ref.col)),
  };
}

/**
 * The hours-cell grammar: blank → 0, non-numeric or negative → an error naming the cell.
 * Anything this accepts, `parseMinor(value, 2)` accepts.
 */
const HOURS_PATTERN = /^\d+(\.\d{1,2})?$/;

/** Is this a legal keystroke-in-progress? `""` and `"1."` must be typeable. */
export function isTypableHours(value: string): boolean {
  return value === "" || value === "." || /^\d*(\.\d{0,2})?$/.test(value);
}

/** Is this a complete, storable value? */
export function isCompleteHours(value: string): boolean {
  return value === "" || HOURS_PATTERN.test(value);
}

export interface ParsedCell {
  ok: true;
  /** `null` means "clear this cell" — the API deletes the row rather than storing zero. */
  hours: string | null;
}
export interface ParsedCellError {
  ok: false;
  error: string;
}

/**
 * Parse one pasted or typed value into what the API expects.
 *
 * Blank clears the cell. A non-numeric or negative value is an error NAMING THE CELL, the
 * same shape the importer produces, so a bad paste tells the planner which cell to look at.
 */
export function parseCellValue(raw: string, label: string): ParsedCell | ParsedCellError {
  const value = raw.trim();
  if (value === "") return { ok: true, hours: null };
  if (value.startsWith("-")) {
    return { ok: false, error: `${label} cannot be negative (${value}).` };
  }
  if (!HOURS_PATTERN.test(value)) {
    return {
      ok: false,
      error: `${label} must be hours — a number with up to 2 decimals. Found "${raw}".`,
    };
  }
  // Normalise so "8" and "8.0" both store as "8.00" and the grid never re-renders a value
  // that differs from what the server will echo back.
  const [whole, frac = ""] = value.split(".");
  return { ok: true, hours: `${whole}.${frac.padEnd(2, "0")}` };
}

// ── Paste ─────────────────────────────────────────────────────────────────────

/**
 * Split a clipboard payload into a rectangle of raw strings.
 *
 * Excel and Sheets both write TSV with `\r\n` or `\n` row separators. A trailing newline
 * is normal and must not produce an empty final row that clears a row of cells.
 */
export function parseTsv(text: string): string[][] {
  const rows = text.replace(/\r\n?/g, "\n").split("\n");
  while (rows.length > 0 && rows[rows.length - 1] === "") rows.pop();
  return rows.map((row) => row.split("\t"));
}

export interface PasteTarget {
  row: number;
  col: number;
  raw: string;
}

/**
 * Where a pasted block lands, written from the anchor and CLAMPED to the grid.
 *
 * Clamping rather than growing: a 12-column paste into the last 3 months writes 3 cells
 * and drops the rest. The alternative — extending the plan's month window because someone
 * pasted — would change the plan's dates as a side effect of a clipboard action.
 * `dropped` counts what fell off so the UI can say so.
 */
export function planPaste(input: {
  block: string[][];
  anchor: CellRef;
  rowCount: number;
  colCount: number;
}): { targets: PasteTarget[]; dropped: number } {
  const targets: PasteTarget[] = [];
  let dropped = 0;

  for (const [dr, blockRow] of input.block.entries()) {
    for (const [dc, raw] of blockRow.entries()) {
      const row = input.anchor.row + dr;
      const col = input.anchor.col + dc;
      if (row >= input.rowCount || col >= input.colCount) {
        dropped += 1;
        continue;
      }
      targets.push({ row, col, raw });
    }
  }
  return { targets, dropped };
}

// ── Fill ──────────────────────────────────────────────────────────────────────

export type FillDirection = "down" | "right";

/**
 * The cells ⌘D / ⌘R writes, and where each reads its value from.
 *
 * Fill down over a multi-row selection repeats the TOP row across the rest; fill right
 * repeats the LEFT column. On a single cell there is nothing to repeat, so both are no-ops
 * — the drag handle is the affordance for extending a single cell.
 */
export function planFill(
  selection: Selection,
  direction: FillDirection,
): Array<{ from: CellRef; to: CellRef }> {
  const rect = selectionRect(selection);
  const out: Array<{ from: CellRef; to: CellRef }> = [];

  if (direction === "down") {
    for (let col = rect.left; col <= rect.right; col += 1) {
      for (let row = rect.top + 1; row <= rect.bottom; row += 1) {
        out.push({ from: { row: rect.top, col }, to: { row, col } });
      }
    }
    return out;
  }

  for (let row = rect.top; row <= rect.bottom; row += 1) {
    for (let col = rect.left + 1; col <= rect.right; col += 1) {
      out.push({ from: { row, col: rect.left }, to: { row, col } });
    }
  }
  return out;
}

/**
 * The cells a corner-handle drag writes.
 *
 * The handle extends the current selection to wherever the pointer is, in ONE axis — the
 * dominant one — because a two-axis fill from a single cell has no obvious source value
 * for the corner. Excel behaves the same way.
 */
export function planHandleDrag(input: {
  selection: Selection;
  pointer: CellRef;
  rowCount: number;
  colCount: number;
}): { targets: Array<{ from: CellRef; to: CellRef }>; extent: CellRef } {
  const rect = selectionRect(input.selection);
  const pointer = clampRef(input.pointer, input.rowCount, input.colCount);

  const downBy = pointer.row - rect.bottom;
  const rightBy = pointer.col - rect.right;

  // Whichever axis the pointer travelled further along wins.
  const vertical = Math.abs(downBy) >= Math.abs(rightBy);

  const targets: Array<{ from: CellRef; to: CellRef }> = [];

  if (vertical) {
    if (downBy <= 0) return { targets, extent: input.selection.extent };
    for (let col = rect.left; col <= rect.right; col += 1) {
      // Each filled row copies the row directly inside the selection edge, so a
      // multi-row selection repeats its own last row rather than its first.
      for (let row = rect.bottom + 1; row <= pointer.row; row += 1) {
        targets.push({ from: { row: rect.bottom, col }, to: { row, col } });
      }
    }
    return { targets, extent: { row: pointer.row, col: input.selection.extent.col } };
  }

  if (rightBy <= 0) return { targets, extent: input.selection.extent };
  for (let row = rect.top; row <= rect.bottom; row += 1) {
    for (let col = rect.right + 1; col <= pointer.col; col += 1) {
      targets.push({ from: { row, col: rect.right }, to: { row, col } });
    }
  }
  return { targets, extent: { row: input.selection.extent.row, col: pointer.col } };
}

// ── Undo ──────────────────────────────────────────────────────────────────────

/** One reversible edit: the key, and what was there before. */
export interface CellPatch {
  key: CellKey;
  previous: string;
}

/**
 * A bounded inverse-patch stack.
 *
 * Session undo only — cleared on save, because after a save the server is the truth and
 * "undo" would mean re-writing hours the server has already accepted, which is an edit,
 * not an undo. 100 entries is roughly a screenful of typing and costs nothing to hold.
 */
export class UndoStack {
  private readonly entries: CellPatch[][] = [];

  constructor(private readonly limit = 100) {}

  push(patches: CellPatch[]): void {
    if (patches.length === 0) return;
    this.entries.push(patches);
    if (this.entries.length > this.limit) this.entries.shift();
  }

  pop(): CellPatch[] | null {
    return this.entries.pop() ?? null;
  }

  clear(): void {
    this.entries.length = 0;
  }

  get depth(): number {
    return this.entries.length;
  }
}
